import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { EmailTemplatesControllerService } from "../../../core/api/controllers/email-templates/email-templates-controller.service";
import {
  PreviewTestEmailRequest,
  TestEmailTemplate,
} from "../../../core/api/controllers/email-templates/data/email-template.models";
import { NotificationService } from "../../../core/services/notification.service";

@Component({
  selector: "app-email-tester",
  templateUrl: "./email-tester.component.html",
  styleUrls: ["./email-tester.component.scss"],
})
export class EmailTesterComponent implements OnInit {
  emailForm: FormGroup;
  templates: TestEmailTemplate[] = [];
  selectedTemplate: TestEmailTemplate;
  previewHtml: SafeHtml;
  previewSubject = "";
  isLoading = false;
  isPreviewLoading = false;
  isSending = false;

  constructor(
    private formBuilder: FormBuilder,
    private emailTemplatesController: EmailTemplatesControllerService,
    private notificationService: NotificationService,
    private sanitizer: DomSanitizer,
  ) {
    this.emailForm = this.formBuilder.group({
      templateId: ["", Validators.required],
      recipientEmail: ["", [Validators.required, Validators.email]],
      templateData: this.formBuilder.group({}),
    });

    this.templateDataForm.valueChanges.subscribe(() => {
      this.previewHtml = null;
      this.previewSubject = "";
    });
  }

  ngOnInit(): void {
    this.loadTemplates();
  }

  get templateDataForm(): FormGroup {
    return this.emailForm.get("templateData") as FormGroup;
  }

  loadTemplates(): void {
    this.isLoading = true;
    this.emailTemplatesController.getTemplates().subscribe(
      (templates) => {
        this.templates = templates;
        this.isLoading = false;

        if (templates.length > 0) {
          const firstTemplateId = templates[0].templateId;
          this.emailForm.get("templateId").setValue(firstTemplateId);
          this.selectTemplate(firstTemplateId);
        }
      },
      (error) => {
        this.isLoading = false;
        this.notificationService.error(error);
      },
    );
  }

  selectTemplate(templateId: string): void {
    this.selectedTemplate = this.templates.find(
      (template) => template.templateId === templateId,
    );

    Object.keys(this.templateDataForm.controls).forEach((key) =>
      this.templateDataForm.removeControl(key),
    );

    this.selectedTemplate?.fields.forEach((field) => {
      this.templateDataForm.addControl(
        field.key,
        this.formBuilder.control(field.value, Validators.required),
      );
    });

    this.previewHtml = null;
    this.previewSubject = "";

    if (this.selectedTemplate) {
      this.refreshPreview();
    }
  }

  refreshPreview(): void {
    if (!this.selectedTemplate || this.templateDataForm.invalid) {
      this.templateDataForm.markAllAsTouched();
      this.notificationService.warn(
        "Complete all template fields to generate a preview.",
      );
      return;
    }

    this.isPreviewLoading = true;
    this.emailTemplatesController
      .previewTemplate(this.buildPreviewRequest())
      .subscribe(
        (preview) => {
          this.previewSubject = preview.subject;
          this.previewHtml = this.sanitizer.bypassSecurityTrustHtml(
            preview.html,
          );
          this.isPreviewLoading = false;
        },
        (error) => {
          this.isPreviewLoading = false;
          this.notificationService.error(error);
        },
      );
  }

  sendTestEmail(): void {
    if (this.emailForm.invalid) {
      this.emailForm.markAllAsTouched();
      return;
    }

    this.isSending = true;
    this.emailTemplatesController
      .sendTestEmail({
        ...this.buildPreviewRequest(),
        recipientEmail: this.emailForm.get("recipientEmail").value,
      })
      .subscribe(
        (response) => {
          this.isSending = false;
          if (response.emailSent) {
            this.notificationService.success("Test email sent successfully.");
          } else {
            this.notificationService.error(
              "The email could not be sent. Check the SMTP configuration.",
            );
          }
        },
        (error) => {
          this.isSending = false;
          this.notificationService.error(error);
        },
      );
  }

  private buildPreviewRequest(): PreviewTestEmailRequest {
    return {
      templateId: this.selectedTemplate.templateId,
      data: this.templateDataForm.getRawValue(),
    };
  }
}
